import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

/**
 * `primary` is near-black, not the org's brand colour. The brand drives
 * accents — selection, focus, active nav — while the one primary action per
 * view stays brand-independent. See the note in tokens.css.
 */
const button = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium ' +
    'transition-[background-color,border-color,color] duration-100 ' +
    'outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/40 ' +
    'disabled:pointer-events-none disabled:opacity-40 select-none',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-action)] text-[var(--color-action-fg)] hover:bg-[var(--color-action-hover)]',
        secondary:
          'bg-[var(--color-surface)] text-[var(--color-fg)] border border-[var(--color-border-strong)] hover:bg-[var(--color-surface-hover)]',
        ghost:
          'text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]',
        subtle:
          'bg-[var(--color-surface-sunken)] text-[var(--color-fg)] hover:bg-[var(--color-surface-active)]',
        danger: 'bg-[var(--color-danger)] text-white hover:brightness-110',
        dangerGhost: 'text-[var(--color-danger)] hover:bg-[var(--color-danger-bg)]',
        link: 'text-[var(--color-fg)] underline underline-offset-2 hover:text-[var(--color-primary)]',
      },
      size: {
        xs: 'h-6 rounded-[var(--radius-xs)] px-1.5 text-2xs',
        sm: 'h-7 rounded-[var(--radius-sm)] px-2.5 text-xs',
        md: 'h-8 rounded-[var(--radius-sm)] px-3 text-xs',
        lg: 'h-10 rounded-[var(--radius-md)] px-4 text-sm',
        icon: 'h-7 w-7 rounded-[var(--radius-sm)]',
        iconSm: 'h-6 w-6 rounded-[var(--radius-xs)]',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      className={cn(button({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  )
})

export { button as buttonVariants }
